import React from 'react'
import { createIntl, FormattedMessage as InnerFormattedMessage } from 'react-intl'
import { AllI18nKeys } from '@/types/i18n'
import { useI18nStore, LanguagesType, getLocale, defaultLang } from './i18n-store'
import { getStringWidthUsingStyle } from './utils'
import { i18n } from '@yt/fe-shared'

const { allI18nLocale } = useI18nStore.getState()
let [locale] = getLocale()

export let intl = createIntl({
  locale: locale,
  messages: (allI18nLocale?.[locale]?.data || {}) as any
})

type InnerFormattedMessagePropsType = React.ComponentProps<typeof InnerFormattedMessage>

//适配，使其表头的文本过长也不会进行换行
export const FormattedTableHeaderMessage = (
  props: InnerFormattedMessagePropsType & {
    id?: AllI18nKeys
    text?: string
    rightSlot?: React.ReactNode
  }
) => {
  const [minWidth, setMinWidth] = React.useState<any>(null)

  React.useEffect(() => {
    const text = props.text || (props.id ? $t(props.id) : '')

    const width = getStringWidthUsingStyle(text)
    setMinWidth(width)
  }, [])

  const rightSlot = props.rightSlot

  return (
    <div
      style={{
        minWidth: minWidth ? minWidth + 'px' : undefined
      }}
    >
      {props?.text ? props.text : <InnerFormattedMessage id={props.id} />}
      {rightSlot ? rightSlot : null}
    </div>
  )
}

export const FormattedMessage = (
  props: InnerFormattedMessagePropsType & {
    id: AllI18nKeys
  }
) => {
  const [defaultMessage, setDefaultMessage] = React.useState('')

  React.useEffect(() => {
    const defaultMessage = getTranslationWithFallback(props.id, allI18nLocale, locale)
    setDefaultMessage(defaultMessage)
  }, [])

  return (
    <div>
      <InnerFormattedMessage {...props} defaultMessage={defaultMessage} />
    </div>
  )
}

export function changeLocale(newLocale: LanguagesType) {
  const allI18nLocale = useI18nStore.getState().allI18nLocale
  i18n.changeLocale(newLocale as LanguagesType)

  if (allI18nLocale[newLocale]) {
    locale = newLocale
    intl = createIntl({
      locale: newLocale,
      messages: allI18nLocale[locale]?.data as any
    })
  }
}

export function useI18n() {
  const { allI18nLocale, currentLang } = useI18nStore(s => ({
    currentLang: s.currentLang,
    allI18nLocale: s.allI18nLocale
  }))

  const $t = (key: string): string => {
    // 获取翻译文本，支持马来西亚语降级到英文
    const defaultMessage = getTranslationWithFallback(key, allI18nLocale, locale)

    return intl.formatMessage({
      id: key,
      defaultMessage: defaultMessage
    }) as string
  }

  return {
    currentLang,
    $t
  }
}

export const $t = (
  key: string,
  opt?: {
    values: any
  }
): string => {
  const allI18nLocale = useI18nStore.getState().allI18nLocale

  // 获取翻译文本，支持马来西亚语降级到英文
  const defaultMessage = getTranslationWithFallback(key, allI18nLocale, locale)

  return intl.formatMessage(
    {
      id: key,
      defaultMessage: defaultMessage
    },
    opt?.values
  )
}

/**
 * 获取翻译文本，支持马来西亚语降级到英文
 * @param key 翻译键
 * @param allI18nLocale 所有语言包数据
 * @param currentLocale 当前语言
 * @returns 翻译文本
 */
function getTranslationWithFallback(
  key: string,
  allI18nLocale: any,
  currentLocale: string
): string {
  // 1. 优先使用当前语言的翻译
  if (allI18nLocale?.[currentLocale]?.data?.[key]) {
    return allI18nLocale[currentLocale].data[key]
  }

  // 2. 如果是马来西亚语，降级到英文
  if (currentLocale === 'ms-MY' && allI18nLocale?.['en-US']?.data?.[key]) {
    return allI18nLocale['en-US'].data[key]
  }

  // 3. 使用默认语言（英文）
  if (allI18nLocale?.[defaultLang]?.data?.[key]) {
    return allI18nLocale[defaultLang].data[key]
  }

  // 4. 如果都没有，返回键名
  return key
}

useI18nStore.subscribe((newState, oldstate) => {
  // 在这里检查特定 key 的变化
  if (newState.currentLang !== oldstate.currentLang) {
    changeLocale(newState.currentLang as LanguagesType)
  }
  if (newState.allI18nLocale !== oldstate.allI18nLocale) {
    const allI18nLocale = useI18nStore.getState().allI18nLocale
    if (newState.currentLang && allI18nLocale[newState.currentLang]?.data) {
      intl.messages = allI18nLocale[newState.currentLang]?.data as any
    }
  }
})
